import React, { useEffect, useMemo } from "react";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { Option } from "./SelectField";
import { createTextValidator } from "../validation/defaultValidators";

export interface CheckboxGroupProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        "type" | "name" | "defaultValue" | "value"
    > {
    label: string;
    name: string;
    options: Option[];
    defaultValue?: string;
    className?: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
    label,
    name,
    options,
    defaultValue,
    className,
    required = false,
    validate,
    validationMessage,
    ...inputProps
}) => {
    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createTextValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [effectiveValidator, name, registerFieldValidation, validationMessage]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        clearFieldError(name);
        inputProps.onChange?.(event);
    };

    return (
        <fieldset
            className={["form-field", className]
                .filter(Boolean)
                .join(" ")}
        >
            <legend>{label}</legend>

                <div className="container wrap">

                    {options.map((option) => (
                        <div className="column column--md-6 column--lg-4" key={option.value}>
                            <span className="wpcf7-form-control-wrap" data-name={name}>
                                <span className="wpcf7-form-control wpcf7-acceptance wpcf7-optional">
                                    <span className="wpcf7-list-item">
                                        <label>
                                            <input
                                                {...inputProps}
                                                type="checkbox"
                                                name={name}
                                                value={option.value}
                                                required={required}
                                                onChange={handleChange}
                                                defaultChecked={option.value === defaultValue}
                                                disabled={option.disabled || inputProps.disabled}
                                                className={[
                                                    fieldError ? "wpcf7-not-valid" : null,
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            />

                                            <span className="wpcf7-list-item-label">{option.label}</span>
                                        </label>
                                    </span>
                                </span>

                                {fieldError ? (
                                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                                        {fieldError}
                                    </span>
                                ) : null}

                            </span>
                        </div>
                    ))}

                </div>

        </fieldset>
    );
};

